/* eslint-disable @typescript-eslint/no-explicit-any */
/**
 * @file BookingConfirmation.page.tsx
 * @path apps/website/src/pages/BookingConfirmation.page.tsx
 * @description Displays booking confirmation after successful Yoco payment.
 * Reads booking data from localStorage using the ref query param.
 */

import { useEffect, useMemo } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { CheckCircle, Calendar, Clock, MapPin, Users } from "lucide-react";

export const BookingConfirmationPage = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();

  const bookingRef = searchParams.get("ref") || searchParams.get("booking");

  const bookingData = useMemo(() => {
    if (!bookingRef) return null;
    try {
      const raw = localStorage.getItem(`booking_${bookingRef}`);
      return raw ? JSON.parse(raw) : null;
    } catch {
      return null;
    }
  }, [bookingRef]);

  useEffect(() => {
    if (!bookingRef) {
      const timeout = setTimeout(() => navigate("/"), 5000);
      return () => clearTimeout(timeout);
    }
  }, [bookingRef, navigate]);

  if (!bookingRef) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background p-6">
        <div className="text-center space-y-4">
          <p className="text-muted-foreground">No booking reference found.</p>
          <p className="text-sm text-muted-foreground">
            Redirecting to home...
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <div className="max-w-2xl mx-auto px-4 py-12 sm:py-20">
        {/* Success Header */}
        <div className="text-center mb-10">
          <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-green-100 mb-6">
            <CheckCircle className="w-8 h-8 text-green-600" />
          </div>
          <h1 className="text-2xl sm:text-3xl font-bold text-foreground mb-2">
            Booking Confirmed!
          </h1>
          <p className="text-muted-foreground">
            Thank you for your booking. A confirmation email will be sent
            shortly.
          </p>
        </div>

        {/* Reference Card */}
        <div className="bg-card border border-border rounded-xl p-6 mb-6 text-center">
          <p className="text-sm text-muted-foreground mb-1">
            Booking Reference
          </p>
          <p className="text-2xl font-bold tracking-widest text-primary">
            {bookingRef}
          </p>
          {bookingData?.paymentReference && (
            <p className="text-xs text-muted-foreground mt-2">
              Payment Ref: {bookingData.paymentReference}
            </p>
          )}
        </div>

        {/* Booking Summary */}
        {bookingData?.bookings?.length > 0 && (
          <div className="space-y-4">
            {bookingData.bookings.map((booking: any, index: number) => (
              <div
                key={booking.id || index}
                className="bg-card border border-border rounded-xl p-5 space-y-3"
              >
                <h3 className="font-semibold text-foreground flex items-center gap-2">
                  <Calendar className="w-4 h-4 text-primary" />
                  Day {index + 1} —{" "}
                  {booking.selectedService?.title || "Booking"}
                </h3>

                <div className="grid grid-cols-2 gap-3 text-sm">
                  <div className="flex items-center gap-2 text-muted-foreground">
                    <Calendar className="w-3.5 h-3.5" />
                    {booking.bookingDate
                      ? new Date(booking.bookingDate).toLocaleDateString(
                          "en-ZA",
                          {
                            weekday: "short",
                            year: "numeric",
                            month: "short",
                            day: "numeric",
                          },
                        )
                      : "—"}
                  </div>
                  <div className="flex items-center gap-2 text-muted-foreground">
                    <Clock className="w-3.5 h-3.5" />
                    {booking.selectedTime || "—"}
                  </div>
                  <div className="flex items-center gap-2 text-muted-foreground">
                    <Users className="w-3.5 h-3.5" />
                    {[
                      booking.adultCount &&
                        `${booking.adultCount} Adult${booking.adultCount > 1 ? "s" : ""}`,
                      booking.childCount &&
                        `${booking.childCount} Child${booking.childCount > 1 ? "ren" : ""}`,
                      booking.infantCount &&
                        `${booking.infantCount} Infant${booking.infantCount > 1 ? "s" : ""}`,
                    ]
                      .filter(Boolean)
                      .join(", ") || "—"}
                  </div>
                  {booking.routeStops?.[0]?.address && (
                    <div className="flex items-center gap-2 text-muted-foreground">
                      <MapPin className="w-3.5 h-3.5" />
                      {booking.routeStops[0].address}
                    </div>
                  )}
                </div>

                <div className="pt-2 border-t border-border text-right">
                  <span className="font-semibold text-foreground">
                    R{booking.pricing?.total?.toFixed(2) || "0.00"}
                  </span>
                </div>
              </div>
            ))}

            {/* Grand Total */}
            <div className="bg-primary/5 border border-primary/20 rounded-xl p-5 flex justify-between items-center">
              <span className="font-medium text-foreground">Total Paid</span>
              <span className="text-xl font-bold text-primary">
                R
                {bookingData.bookings
                  .reduce(
                    (sum: number, b: any) => sum + (b.pricing?.total || 0),
                    0,
                  )
                  .toFixed(2)}
              </span>
            </div>
          </div>
        )}

        {/* Actions */}
        <div className="mt-8 text-center">
          <button
            onClick={() => navigate("/")}
            className="px-6 py-3 bg-primary text-primary-foreground rounded-lg font-medium hover:bg-primary/90 transition-colors"
          >
            Back to Home
          </button>
        </div>
      </div>
    </div>
  );
};
